/**
 * Absolute Value Equations generator.
 *
 * Forms like |x| = k, |ax + b| = k, and c|ax + b| + d = e. Choose the two
 * roots (or the no-solution case) first, then build a valid equation around
 * them. Correctness is verified by independently substituting each claimed
 * root back into the absolute-value expression - a separate code path from
 * construction.
 *
 * Answer shapes:
 *   - two distinct roots  -> SetAnswer
 *   - a single root (|expr| = 0) -> NumberAnswer
 *   - no solution (|expr| = negative) -> SpecialAnswer("no-solution")
 *
 * Families:
 *   A1: |x| = k
 *   A2: |x + b| = k
 *   A3: |ax + b| = k
 *   A4: c|ax + b| + d = e   (isolate the absolute value first)
 *   A5: |ax + b| = |cx + d| (two absolute values; Big Ideas Math 1.4). Solved
 *       via the related equations ax+b = cx+d OR ax+b = -(cx+d).
 */

import { Rational, rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { makeSetAnswer, type AnswerValue } from "../engine/answer";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Hint,
  Question,
  QuestionSignature,
  SolutionStep,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "absolute-value-equations" as const;
const VERSION = "1.0.0";
const VARIABLES = ["x", "n", "a", "m", "p", "y"] as const;

// A5 is |ax + b| = |cx + d| (two absolute values), taught in Big Ideas 1.4.
type Family = "A1" | "A2" | "A3" | "A4" | "A5";

interface Cfg {
  readonly families: readonly Family[];
  readonly coeffMax: number;
  readonly innerMax: number;
  readonly rhsMax: number;
  readonly pNeg: number;
  /** Probability of deliberately generating a no-solution case. */
  readonly pNoSolution: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["A1", "A2"], coeffMax: 1, innerMax: 8, rhsMax: 10, pNeg: 0.2, pNoSolution: 0 },
  standard: { families: ["A2", "A3"], coeffMax: 4, innerMax: 9, rhsMax: 12, pNeg: 0.4, pNoSolution: 0.1 },
  hard: { families: ["A3", "A4", "A5"], coeffMax: 6, innerMax: 10, rhsMax: 14, pNeg: 0.5, pNoSolution: 0.15 },
  challenge: { families: ["A4", "A5"], coeffMax: 8, innerMax: 12, rhsMax: 16, pNeg: 0.55, pNoSolution: 0.2 },
};

/** |a*x + b| evaluated at x. */
function absValue(a: Rational, b: Rational, x: Rational): Rational {
  return a.mul(x).add(b).abs();
}

interface Built {
  family: Family;
  variable: string;
  displayLatex: string;
  answer: AnswerValue;
  steps: SolutionStep[];
  hints: Hint[];
  /** For independent validation: the inner (a,b) and the target |.| value k. */
  a: Rational;
  b: Rational;
  k: Rational;
  category: "two" | "one" | "none";
  roots: Rational[];
  /** For A5 |ax+b| = |cx+d|: the right-hand inner (c, d). Absent otherwise. */
  c?: Rational;
  d?: Rational;
}

function absLatex(a: Rational, b: Rational, v: string): string {
  const parts: string[] = [];
  if (a.equals(Rational.ONE)) parts.push(v);
  else if (a.equals(Rational.of(-1n))) parts.push(`-${v}`);
  else parts.push(`${a.toLatex()}${v}`);
  if (!b.isZero()) parts.push(b.isNegative() ? `- ${b.neg().toLatex()}` : `+ ${b.toLatex()}`);
  return `\\left|${parts.join(" ")}\\right|`;
}

function build(rng: Rng, difficulty: Difficulty): Built | null {
  const cfg = DIFF[difficulty];
  const v = rng.pick(VARIABLES);
  const family = rng.pick(cfg.families);

  if (family === "A5") {
    return buildTwoAbsolutes(rng, cfg, v);
  }

  // Inner expression a*x + b.
  const a =
    family === "A1" || family === "A2"
      ? Rational.ONE
      : rat(rng.int(2, cfg.coeffMax < 2 ? 2 : cfg.coeffMax) * rng.sign(cfg.pNeg));
  const b = family === "A1" ? Rational.ZERO : rat(rng.int(1, cfg.innerMax) * rng.sign(cfg.pNeg));

  const noSolution = rng.next() < cfg.pNoSolution;

  if (noSolution) {
    // |inner| = negative  -> no solution. For A4, c*|.|+d=e with (e-d)/c < 0.
    const k = rat(-rng.int(1, cfg.rhsMax)); // negative target magnitude
    const built = assemble(family, v, a, b, k, cfg, rng);
    if (!built) return null;
    built.category = "none";
    built.answer = { kind: "special", value: "no-solution" };
    built.roots = [];
    built.steps = [
      built.steps[0] as SolutionStep,
      {
        description: "An absolute value cannot equal a negative number, so there is no solution.",
        latex: `${absLatex(a, b, v)} = ${k.toLatex()}`,
      },
    ].filter(Boolean) as SolutionStep[];
    return built;
  }

  // Choose k >= 0. k = 0 gives a single root; k > 0 gives two.
  const kMag = rng.int(0, cfg.rhsMax);
  const k = rat(kMag);
  const built = assemble(family, v, a, b, k, cfg, rng);
  if (!built) return null;

  // Roots: a*x + b = k  or  a*x + b = -k  ->  x = (±k - b)/a.
  const r1 = k.sub(b).div(a);
  const r2 = k.neg().sub(b).div(a);
  if (kMag === 0) {
    built.category = "one";
    built.roots = [r1];
    built.answer = { kind: "number", value: r1 };
  } else {
    built.category = "two";
    built.roots = [r1, r2];
    built.answer = makeSetAnswer([r1, r2]);
  }
  return built;
}

/**
 * Build |ax + b| = |cx + d| (Big Ideas 1.4, "two absolute values").
 *
 * Solved by the two related linear equations:
 *   ax + b = cx + d        -> (a - c)x = d - b
 *   ax + b = -(cx + d)     -> (a + c)x = -d - b
 * We choose coefficients so both (a - c) and (a + c) are nonzero, giving two
 * finite roots; identical roots are deduped to a single solution. Roots are
 * computed here and verified independently by substitution in verify().
 */
function buildTwoAbsolutes(rng: Rng, cfg: Cfg, v: string): Built | null {
  const a = rat(rng.int(2, cfg.coeffMax < 2 ? 2 : cfg.coeffMax) * rng.sign(cfg.pNeg));
  let c = rat(rng.int(1, cfg.coeffMax < 2 ? 2 : cfg.coeffMax) * rng.sign(cfg.pNeg));
  const b = rat(rng.int(1, cfg.innerMax) * rng.sign(cfg.pNeg));
  const d = rat(rng.int(1, cfg.innerMax) * rng.sign(cfg.pNeg));

  // Require a - c != 0 and a + c != 0 so both related equations are solvable.
  if (a.equals(c) || a.equals(c.neg()) || a.isZero() || c.isZero()) return null;

  // Root 1: (a - c)x = d - b.
  const r1 = d.sub(b).div(a.sub(c));
  // Root 2: (a + c)x = -d - b.
  const r2 = d.neg().sub(b).div(a.add(c));

  const same = r1.equals(r2);
  const roots = same ? [r1] : [r1, r2];

  const leftL = absLatex(a, b, v);
  const rightL = absLatex(c, d, v);
  const negInnerL = signedInner(c.neg(), d.neg(), v);

  const steps: SolutionStep[] = [
    {
      description:
        "When two absolute values are equal, the inside expressions are equal or opposite. Write both related equations.",
      latex: `${signedInner(a, b, v)} = ${signedInner(c, d, v)} \\quad\\text{or}\\quad ${signedInner(a, b, v)} = ${negInnerL}`,
    },
    {
      description: same ? "Both equations give the same solution." : "Solve each equation.",
      latex: same
        ? `${v} = ${r1.toLatex()}`
        : `${v} = ${r1.toLatex()} \\quad\\text{or}\\quad ${v} = ${r2.toLatex()}`,
    },
  ];

  const hints: Hint[] = [
    { level: 1, text: "Two equal absolute values mean the insides are equal or opposite." },
    { level: 2, text: "Write ax+b = cx+d and ax+b = -(cx+d), then solve each." },
  ];

  return {
    family: "A5",
    variable: v,
    displayLatex: `${leftL} = ${rightL}`,
    answer: same ? { kind: "number", value: r1 } : makeSetAnswer(roots),
    steps,
    hints,
    a,
    b,
    k: Rational.ZERO, // unused for A5; verify() branches on family via c/d presence
    category: same ? "one" : "two",
    roots,
    c,
    d,
  };
}

/** Signed linear inner "ax + b" for step display (handles 1/-1 and sign of b). */
function signedInner(a: Rational, b: Rational, v: string): string {
  const parts: string[] = [];
  if (a.equals(Rational.ONE)) parts.push(v);
  else if (a.equals(Rational.of(-1n))) parts.push(`-${v}`);
  else parts.push(`${a.toLatex()}${v}`);
  if (!b.isZero()) parts.push(b.isNegative() ? `- ${b.neg().toLatex()}` : `+ ${b.toLatex()}`);
  return parts.join(" ");
}

/** Assemble display + isolation steps; returns partial Built (roots/answer set by caller). */
function assemble(
  family: Family,
  v: string,
  a: Rational,
  b: Rational,
  k: Rational,
  cfg: Cfg,
  rng: Rng,
): Built | null {
  const steps: SolutionStep[] = [];
  const hints: Hint[] = [];
  let displayLatex: string;

  if (family === "A4") {
    // c|inner| + d = e, with c != 0, arranged so (e - d)/c = k.
    const c = rat(rng.int(2, cfg.coeffMax < 2 ? 2 : cfg.coeffMax) * (rng.bool() ? 1 : -1));
    const d = rat(rng.int(1, cfg.rhsMax) * rng.sign(cfg.pNeg));
    const e = c.mul(k).add(d);
    const inner = absLatex(a, b, v);
    displayLatex = `${cFactor(c)}${inner} ${plusL(d)} = ${e.toLatex()}`;
    steps.push({
      description: "Isolate the absolute value expression.",
      latex: `${inner} = ${k.toLatex()}`,
    });
    hints.push({ level: 1, text: "First isolate the absolute value by undoing the outside operations." });
  } else {
    displayLatex = `${absLatex(a, b, v)} = ${k.toLatex()}`;
    steps.push({
      description: "The expression inside the bars equals the value or its opposite.",
      latex: `${absLatex(a, b, v)} = ${k.toLatex()}`,
    });
    hints.push({ level: 1, text: "Set the inside equal to the value AND to its opposite." });
  }

  return {
    family,
    variable: v,
    displayLatex,
    answer: { kind: "special", value: "no-solution" }, // placeholder, set by caller
    steps,
    hints,
    a,
    b,
    k,
    category: "none",
    roots: [],
  };
}

function cFactor(c: Rational): string {
  if (c.equals(Rational.ONE)) return "";
  if (c.equals(Rational.of(-1n))) return "-";
  return c.toLatex();
}
function plusL(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toLatex()}` : `+ ${r.toLatex()}`;
}

/** Independently verify the built instance by substitution. */
function verify(b: Built): boolean {
  // A5: |a x + b| = |c x + d|. Each root must make both sides' magnitudes equal.
  if (b.family === "A5") {
    if (b.c === undefined || b.d === undefined) return false;
    if (b.roots.length < 1) return false;
    for (const root of b.roots) {
      if (!absValue(b.a, b.b, root).equals(absValue(b.c, b.d, root))) return false;
    }
    if (b.category === "two") {
      return b.roots.length === 2 && !b.roots[0]!.equals(b.roots[1]!);
    }
    return b.roots.length === 1;
  }

  if (b.category === "none") {
    // k < 0 required for the |.| = k no-solution framing.
    return b.k.isNegative();
  }
  // Every claimed root must make |a*x + b| equal k (>= 0).
  if (b.k.isNegative()) return false;
  for (const root of b.roots) {
    if (!absValue(b.a, b.b, root).equals(b.k)) return false;
  }
  // Two-solution roots must be distinct; one-solution must be a single root.
  if (b.category === "two") {
    return b.roots.length === 2 && !b.roots[0]!.equals(b.roots[1]!);
  }
  return b.roots.length === 1;
}

export class AbsoluteValueEquationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 200; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const built = build(rng, ctx.difficulty);
      if (!built) continue;
      if (!verify(built)) continue;
      // Quality: avoid trivial |x| = 0 style at anything above easy.
      if (built.category === "one" && ctx.difficulty !== "easy") continue;

      const answerType = built.answer.kind === "set" ? "set" : built.answer.kind === "special" ? "special" : "number";
      return {
        id: `${TOPIC}-${ctx.seed}-${built.family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family: built.family,
        difficulty: ctx.difficulty,
        prompt: { text: built.displayLatex, latex: built.displayLatex },
        answerType: answerType as Question["answerType"],
        answer: built.answer,
        steps: built.steps,
        hints: built.hints,
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 200 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "set"
        ? q.answer.values.map((v) => v.key()).join(",")
        : q.answer.kind === "number"
          ? q.answer.value.key()
          : "none";
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const absoluteValueEquations = new AbsoluteValueEquationsGenerator();
